import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, useMap, ScaleControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import mapStartIcon from '../assets/map-start.svg';
import mapPlaneIcon from '../assets/map-plane.svg';
import mapBallonIcon from '../assets/map-ballon.svg';

interface Coordinate {
  latitude: number;
  longitude: number;
  altitude: number;
  timestamp: string;
}

interface FlightRoute {
  _id: string;
  coordinates?: Coordinate[];
  aircraftid?: { type: string } | null;
}

interface MultiRouteMapProps {
  flights: FlightRoute[];
  selectedId?: string | null;
  onFlightSelect?: (id: string) => void;
  height?: string;
}

const FitAllBounds: React.FC<{ flights: FlightRoute[] }> = ({ flights }) => {
  const map = useMap();

  useEffect(() => {
    const allCoords: [number, number][] = [];
    for (const f of flights) {
      if (!f.coordinates) continue;
      for (const c of f.coordinates) {
        allCoords.push([c.latitude, c.longitude]);
      }
    }
    if (allCoords.length > 0) {
      map.fitBounds(allCoords, { padding: [40, 40] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flights]);

  return null;
};

const MultiRouteMap: React.FC<MultiRouteMapProps> = ({
  flights,
  selectedId,
  onFlightSelect,
  height = '300px',
}) => {
  const flightsWithCoords = flights.filter(
    (f): f is FlightRoute & { coordinates: Coordinate[] } => Boolean(f.coordinates && f.coordinates.length > 0)
  );

  if (flightsWithCoords.length === 0) {
    return (
      <div style={{
        width: '100%',
        height,
        background: '#f0f0f0',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <span style={{ color: '#687088' }}>Keine Flugrouten verfügbar</span>
      </div>
    );
  }

  const firstCoord = flightsWithCoords[0].coordinates[0];

  return (
    <div style={{ borderRadius: '8px', overflow: 'hidden', height }}>
      <MapContainer
        center={[firstCoord.latitude, firstCoord.longitude]}
        zoom={8}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <TileLayer
          attribution='&copy; <a href="https://www.openflightmaps.org/">open flightmaps association</a>'
          url="https://nwy-tiles-api.prod.newaydata.com/tiles/{z}/{x}/{y}.png?path=2602/aero/latest"
          minZoom={7}
          maxZoom={13}
          opacity={0.6}
        />
        {flightsWithCoords.map(f => {
          const isSelected = f._id === selectedId;
          const positions = f.coordinates.map(c => [c.latitude, c.longitude] as [number, number]);
          const startCoord = f.coordinates[0];
          const endCoord = f.coordinates[f.coordinates.length - 1];

          const startMarker = L.icon({ iconUrl: mapStartIcon, iconSize: [32, 32], iconAnchor: [16, 16] });
          const aircraftMarker = L.icon({
            iconUrl: f.aircraftid?.type === 'balloon' ? mapBallonIcon : mapPlaneIcon,
            iconSize: [32, 32],
            iconAnchor: [16, 16],
          });

          return (
            <React.Fragment key={f._id}>
              <Polyline
                positions={positions}
                color="#0d6efd"
                weight={isSelected ? 4 : 2}
                opacity={isSelected ? 0.9 : 0.5}
                dashArray="5, 5"
                eventHandlers={{ click: () => onFlightSelect?.(f._id) }}
              />
              <Marker
                position={[startCoord.latitude, startCoord.longitude]}
                icon={startMarker}
                eventHandlers={{ click: () => onFlightSelect?.(f._id) }}
              />
              <Marker
                position={[endCoord.latitude, endCoord.longitude]}
                icon={aircraftMarker}
                eventHandlers={{ click: () => onFlightSelect?.(f._id) }}
              />
            </React.Fragment>
          );
        })}

        <ScaleControl position="bottomright" metric={true} imperial={false} maxWidth={150} />

        <FitAllBounds flights={flightsWithCoords} />
      </MapContainer>
    </div>
  );
};

export default MultiRouteMap;
